import React, { useEffect, useMemo, useState } from 'react';
import axiosInstant from '../../../../api';
import {
  getProductCategories,
  rmnBrandsEndpoint,
  rmnCategoriesEndpoint,
} from '../../../../api/endpoints';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import { RmnCategoryNode, RmnNetworkBrand } from '../../../../types/rmn';

export interface FlatCategoryOption {
  id: number;
  label: string;
  depth: number;
}

export interface TdcCategoryRow {
  id: number;
  name: string;
  parent_id: number | null;
}

// Depth-first flatten of the flat TDC category list into indented options.
// Shared with the Strategies-list filters (TDC-1499).
export function flattenTdc(rows: TdcCategoryRow[]): FlatCategoryOption[] {
  const byParent = new Map<number | null, TdcCategoryRow[]>();
  rows.forEach((r) => {
    const key = r.parent_id != null ? r.parent_id : null;
    const bucket = byParent.get(key) || [];
    bucket.push(r);
    byParent.set(key, bucket);
  });
  const out: FlatCategoryOption[] = [];
  const seen = new Set<number>();
  const walk = (parentId: number | null, depth: number) => {
    (byParent.get(parentId) || []).forEach((c) => {
      if (seen.has(c.id)) return;
      seen.add(c.id);
      out.push({ id: c.id, label: c.name, depth });
      walk(c.id, depth + 1);
    });
  };
  walk(null, 0);
  // Orphans (missing parent rows) still deserve an entry.
  rows.forEach((r) => {
    if (!seen.has(r.id)) out.push({ id: r.id, label: r.name, depth: 0 });
  });
  return out;
}

// Depth-first flatten of the network's nested category tree into indented
// options. The tree already arrives nested (GET /rmn/categories), unlike the
// flat TDC list above. Shared with the Discovered Products filters.
export function flattenRmn(
  nodes: RmnCategoryNode[],
  out: FlatCategoryOption[] = []
): FlatCategoryOption[] {
  nodes.forEach((node) => {
    out.push({ id: node.id, label: node.name, depth: node.depth || 0 });
    if (node.children && node.children.length > 0) flattenRmn(node.children, out);
  });
  return out;
}

export const filterSelectClass =
  "text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2 py-1.5 bg-surface cursor-pointer max-w-[190px]";

export type ProductFilterKey =
  | "product_category_id"
  | "rmn_brand_id"
  | "rmn_category_id";

// Products-list filters (TDC-1499). Three independent dropdowns, ANDed
// server-side (getBrandProducts pushes each into one Op.and, both category
// filters matching a whole subtree):
// - TDC taxonomy — every imported product carries one;
// - the network's OWN crawled categories (the retailer's taxonomy), which
//   only cover crawl-linked products, so picking one narrows to those;
// - the account's sub brands (brands_rmn_brands suppliers).
// Each dropdown renders only when it has options, so a non-RMN brand (empty
// category tree, no suppliers) simply sees the TDC one. The Categories
// page's "view products" deep link sets rmn_category_id, which shows up
// here as the network dropdown's selected value.
export function ProductFilters({
  productCategoryId,
  brandId,
  rmnCategoryId,
  onChange,
}: {
  productCategoryId: string;
  brandId: string;
  rmnCategoryId: string;
  onChange: (key: ProductFilterKey, value: string | null) => void;
}) {
  const { brandId: accountBrandId, brandName } = useNetworkScope();
  const [tdcRows, setTdcRows] = useState<TdcCategoryRow[]>([]);
  const [rmnTree, setRmnTree] = useState<RmnCategoryNode[]>([]);
  const [brands, setBrands] = useState<RmnNetworkBrand[]>([]);

  useEffect(() => {
    let stale = false;
    axiosInstant
      .get(getProductCategories())
      .then((res) => {
        if (!stale) setTdcRows((res.data || []) as TdcCategoryRow[]);
      })
      .catch(() => {
        if (!stale) setTdcRows([]);
      });
    axiosInstant
      .get(rmnCategoriesEndpoint)
      .then((res) => {
        if (!stale) setRmnTree((res.data || []) as RmnCategoryNode[]);
      })
      .catch(() => {
        if (!stale) setRmnTree([]);
      });
    axiosInstant
      .get(rmnBrandsEndpoint)
      .then((res) => {
        if (stale) return;
        // The endpoint returns the account brand itself too — the filter is
        // for its sub brands only.
        const rows = (res.data || []) as RmnNetworkBrand[];
        setBrands(rows.filter((b) => b.id !== accountBrandId));
      })
      .catch(() => {
        if (!stale) setBrands([]);
      });
    return () => {
      stale = true;
    };
  }, [accountBrandId]);

  const categories = useMemo(() => flattenTdc(tdcRows), [tdcRows]);
  const rmnCategories = useMemo(() => flattenRmn(rmnTree), [rmnTree]);
  const networkLabel = brandName || 'Network';
  // A deep link can name a category the tree no longer returns (deactivated
  // or removed). Keep the filter visible and clearable rather than showing a
  // blank select that silently narrows the list.
  const rmnCategoryMissing =
    !!rmnCategoryId &&
    !rmnCategories.some((o) => String(o.id) === rmnCategoryId);

  if (categories.length === 0 && rmnCategories.length === 0 && brands.length === 0)
    return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {categories.length > 0 && (
        <select
          aria-label="Filter by TDC category"
          value={productCategoryId}
          onChange={(e) =>
            onChange("product_category_id", e.target.value || null)
          }
          className={filterSelectClass}
        >
          <option value="">All TDC categories</option>
          {categories.map((o) => (
            <option key={o.id} value={o.id}>
              {'\u00A0'.repeat(o.depth * 3)}
              {o.label}
            </option>
          ))}
        </select>
      )}
      {(rmnCategories.length > 0 || rmnCategoryMissing) && (
        <select
          aria-label={`Filter by ${networkLabel} category`}
          value={rmnCategoryId}
          onChange={(e) => onChange('rmn_category_id', e.target.value || null)}
          className={filterSelectClass}
        >
          <option value="">{`All ${networkLabel} categories`}</option>
          {rmnCategoryMissing && (
            <option value={rmnCategoryId}>Selected category</option>
          )}
          {rmnCategories.map((o) => (
            <option key={o.id} value={o.id}>
              {'\u00A0'.repeat(o.depth * 3)}
              {o.label}
            </option>
          ))}
        </select>
      )}
      {brands.length > 0 && (
        <select
          aria-label="Filter by brand"
          value={brandId}
          onChange={(e) => onChange("rmn_brand_id", e.target.value || null)}
          className={filterSelectClass}
        >
          <option value="">All catalog brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
